import React, { useEffect } from 'react';
import { Heart, Users, Lightbulb, Trophy, ArrowRight, LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardContent } from '../../components/shared/Card';
import { Section, SectionHeader } from '../../components/shared/Section';
import { TagList } from '../../components/shared/TagSystem';
import { cn } from '../../lib/utils';
import { getThemeClass } from '../../lib/theme';
import { HeroRibbon } from '../Home/components/HeroSection';
import PageLayout from '../../layouts/PageLayout';

interface ImpactArea {
  title: string;
  icon: LucideIcon;
  description: string;
  achievements: string[];
}

interface ImpactMetric {
  title: string;
  icon: LucideIcon;
  metrics: string[];
}

const CommunityImpact = () => {
  const impactAreas: ImpactArea[] = [
    {
      title: "Youth Education & Mentorship",
      icon: Heart,
      description: "Led weekly children's assemblies focusing on character development and cultural education",
      achievements: [
        "Designed interactive learning sessions",
        "Facilitated discussion-based learning modules",
        "Created engaging environments for complex topics",
        "Mentored youth through developmental stages",
        "Developed curriculum bridging cultural contexts"
      ]
    },
    {
      title: "Community Engagement",
      icon: Users,
      description: "Coordinated and executed community-wide events while managing logistics for cultural and educational programs",
      achievements: [
        "Coordinated community-wide events",
        "Managed program logistics",
        "Led outreach initiatives",
        "Facilitated community communication",
        "Supported service initiatives"
      ]
    },
    {
      title: "Organizational Development",
      icon: Lightbulb,
      description: "Applied technical expertise to improve community operations and streamline processes",
      achievements: [
        "Streamlined administrative processes",
        "Enhanced organizational efficiency",
        "Improved community engagement systems",
        "Applied professional skills for social benefit"
      ]
    }
  ];

  const impactMetrics: ImpactMetric[] = [
    {
      title: "Leadership Impact",
      icon: Trophy,
      metrics: [
        "Youth Development: Successfully guided community members through formative years",
        "Community Building: Strengthened bonds through active engagement",
        "Process Improvement: Enhanced organizational efficiency",
        "Cultural Bridge: Facilitated traditional-modern understanding"
      ]
    }
  ];

  const demonstratedSkills = [
    "Leadership in Non-Technical Contexts",
    "Group Management & Mentoring",
    "Complex Program Organization",
    "Community Development",
    "Professional Expertise Application",
    "Cultural Integration",
    "Event Planning",
    "Educational Program Design"
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroRibbon
        title="Community Impact"
        subtitle="Dedicated to fostering community growth through volunteer leadership, youth development, and organizational excellence at BAPS Charities."
      />

      {/* Impact Areas */}
      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader
          title="Areas of Impact"
          subtitle="Key focus areas where technical expertise meets community service"
          align="left"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          {impactAreas.map((area, idx) => (
              <Card
                key={`card-${idx}`}
                className={cn(
                  "h-full transition-all duration-300",
                  "hover:shadow-lg",
                  getThemeClass('colors.border.hover', 'borderHover')
                )}
              >
                <CardHeader
                  icon={area.icon}
                  title={area.title}
                  subtitle={area.description}
                  iconClassName={cn(
                    "w-6 h-6",
                    getThemeClass('colors.primary.main')
                  )}
                />
                <CardContent>
                  <ul className="space-y-3">
                    {area.achievements.map((achievement, i) => (
                      <li
                        key={i}
                        className="flex items-start group"
                      >
                        <span className={cn(
                          "w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0",
                          "transition-all duration-300",
                          "group-hover:scale-125",
                          getThemeClass('colors.primary.surface')
                        )} />
                        <span className={cn(
                          getThemeClass('colors.text.secondary'),
                          "transition-colors duration-300",
                          "group-hover:text-slate-900"
                        )}>
                          {achievement}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
          ))}
        </div>
      </Section>

      {/* Impact Metrics */}
      <Section
        background="light"
        spacing="sm"
        className={cn(
          getThemeClass('colors.background.light'),
          'relative'
        )}
        withDivider={true}
      >        
        
          <SectionHeader
            title="Leadership Impact"
            subtitle="Measurable outcomes and achievements in community development"
            align="left"
          />
          
          <div className="grid grid-cols-1 gap-8 mb-10">
            {impactMetrics.map((metric, idx) => (
              <Card
                key={idx}
                variant="bordered"
                className={cn(
                  "transition-all duration-300",
                  getThemeClass('colors.border.hover', 'borderHover')
                )}
              >
                <CardHeader
                  icon={metric.icon}
                  title={metric.title}
                  iconClassName={cn(
                    "w-6 h-6",
                    getThemeClass('colors.success.main')
                  )}
                />
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {metric.metrics.map((item, i) => (
                      <div
                        key={i}
                        className="flex items-start group"
                      >
                        <span className={cn(
                          "w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0",
                          "transition-all duration-300",
                          "group-hover:scale-125",
                          getThemeClass('colors.success.surface')
                        )} />
                        <span className={cn(
                          getThemeClass('colors.text.secondary'),
                          "transition-colors duration-300",
                          "group-hover:text-slate-900"
                        )}>
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
      </Section>

      {/* Skills Demonstration */}
      <Section background="light" spacing="sm">
        <SectionHeader
          title="Demonstrated Capabilities"
          subtitle="Key skills and competencies demonstrated through community service"
          align="left"
        />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <TagList
            tags={demonstratedSkills.map(skill => ({
              label: skill,
              variant: 'primary',
              size: 'md'
            }))}
            spacing="md"
          />
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={cn(
            "mt-12 text-center",
            "p-8 rounded-lg",
            "border",
            getThemeClass('colors.border.light'),
            getThemeClass('colors.background.surface')
          )}
        >
          <h3 className={cn(
            "text-2xl font-bold mb-4",
            getThemeClass('colors.text.primary')
          )}>
            Interested in Community Collaboration?
          </h3>
          <p className={cn(
            "text-lg mb-6",
            getThemeClass('colors.text.secondary')
          )}>
            Connect to explore opportunities for technical mentorship and community development initiatives.
          </p>
          <a
            href="/contact"
            className={cn(
              "inline-flex items-center",
              "px-6 py-3 rounded-lg",
              "text-white font-medium",
              "transition-all duration-300",
              "hover:shadow-lg",
              getThemeClass('colors.primary.surface'),
              getThemeClass('colors.primary.main')
            )}
          >
            Get in Touch
            <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </motion.div>
      </Section>
    </PageLayout>
  );
};

export default CommunityImpact;